import { NodeType } from 'src/modules/exam-preparation/graphql/common/types/node';
import { getExamLevelSubTreeSyncBox, getExamLevelSyncBox } from 'src/modules/exam-preparation/offline/sync-boxes/examLevel';
import { TableName } from '../dexie-db/constants';
import db from '../dexie-db/db';
import { imageCacheManager } from './image-cache/ImageCacheManager';
import SyncBoxConfig from './SyncBoxConfig';
import SyncBoxState from './SyncBoxState';
/**
 * Deletion workflow for qnaNode table
 * - Delete all cached images for Diagram nodes
 * - Delete related syncBoxes (examLevelSubTree, examLevel)
 */
export class QnaNodeDeletionWorkflow {
    async execute(syncBoxConfig) {
        // Validate if this is an examLevelSubTree syncBox
        const isExamLevelSubTree = await this.validateExamLevelSubTreeSyncBox(syncBoxConfig);
        if (!isExamLevelSubTree) {
            return;
        }
        const examLevelSubTreeSyncBoxId = syncBoxConfig.id;
        // Delete cached images for Diagram nodes
        await imageCacheManager.deleteCachedImagesForSyncBox(examLevelSubTreeSyncBoxId);
        // Extract examLevelId from the syncBox filters to delete related syncBoxes
        await this.deleteRelatedSyncBoxes(syncBoxConfig);
    }
    async validateExamLevelSubTreeSyncBox(syncBoxConfig) {
        const examLevelId = this.extractExamLevelId(syncBoxConfig);
        if (examLevelId) {
            const examLevelNode = await db.qnaNode.get(examLevelId);
            if (examLevelNode) {
                return examLevelNode.type === NodeType.EXAM_LEVEL;
            }
        }
        return false;
    }
    extractExamLevelId(syncBoxConfig) {
        const filters = syncBoxConfig.pullConfig.filters;
        // Check if this is an examLevelSubTree syncBox (has ancestors filter)
        if (filters?.ancestors) {
            return filters.ancestors;
        }
        return null;
    }
    async deleteRelatedSyncBoxes(syncBoxConfig) {
        try {
            const examLevelId = this.extractExamLevelId(syncBoxConfig);
            if (!examLevelId) {
                return;
            }
            // Delete examLevelSubTree syncBox
            const examLevelSubTreeSyncBox = getExamLevelSubTreeSyncBox(examLevelId);
            await examLevelSubTreeSyncBox.init();
            await this.deleteSyncBoxAndData((await (examLevelSubTreeSyncBox.getConfig())).syncBoxId);
            // Delete examLevel syncBox
            const examLevelSyncBox = getExamLevelSyncBox(examLevelId);
            await examLevelSyncBox.init();
            await this.deleteSyncBoxAndData((await (examLevelSyncBox.getConfig())).syncBoxId);
        }
        catch (error) {
            console.error('Error deleting related syncBoxes:', error);
        }
    }
    async deleteNodesWithSyncBoxId(syncBoxId) {
        try {
            // Delete all nodes with this syncBoxId
            await db.qnaNode
                .where('_syncBoxId')
                .equals(syncBoxId)
                .delete();
        }
        catch (error) {
            console.error('Error deleting nodes with syncBoxId:', error);
        }
    }
    async deleteSyncBoxAndData(syncBoxId) {
        try {
            // Delete all nodes with this syncBoxId
            await this.deleteNodesWithSyncBoxId(syncBoxId);
            // Delete syncBox state
            await SyncBoxState.delete(syncBoxId);
            // Delete syncBox config
            await SyncBoxConfig.delete(syncBoxId);
        }
        catch (error) {
            console.error('Error deleting syncBox and data:', error);
        }
    }
}
/**
 * Factory for creating deletion workflows based on table name
 */
export class DeletionWorkflowFactory {
    static workflows = new Map([
        [TableName.QNA_NODE, new QnaNodeDeletionWorkflow()],
    ]);
    static getWorkflow(tableName) {
        return this.workflows.get(tableName) || null;
    }
    static registerWorkflow(tableName, workflow) {
        this.workflows.set(tableName, workflow);
    }
}
